import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { stories, grammarTopics } from '../data/mock';
import { verbConjugations, quizData, keywordsData, grammarExplanations } from '../data/conjugations';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import TextToSpeech from '../components/TextToSpeech';
import SynchronizedTextReader from '../components/SynchronizedTextReader';
import VerbConjugationPanel from '../components/VerbConjugationPanel';
import GrammarExplanationModal from '../components/GrammarExplanationModal';
import GrammarExplanationPanel from '../components/GrammarExplanationPanel';
import QuizComponent from '../components/QuizComponent';
import KeywordsComponent from '../components/KeywordsComponent';
import { ArrowLeft, BookOpen, Volume2, Play, CheckCircle2, Circle, Brain, Target, Settings, MessageSquare, Key, HelpCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const StoryReader = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addSelectedWord, selectedWords } = useLanguage();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [selectedWordsList, setSelectedWordsList] = useState([]);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showGrammarPanel, setShowGrammarPanel] = useState(false);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [activeTab, setActiveTab] = useState('story');
  const [selectedVerb, setSelectedVerb] = useState(null);
  const [showGrammarModal, setShowGrammarModal] = useState(false);
  const [selectedGrammarExplanation, setSelectedGrammarExplanation] = useState(null);
  const [selectedGrammarText, setSelectedGrammarText] = useState('');
  const [userTrainingItems, setUserTrainingItems] = useState([]);
  const [showGrammarExplanationPanel, setShowGrammarExplanationPanel] = useState(false);

  useEffect(() => {
    const foundStory = stories.find(s => s.id === parseInt(id));
    if (foundStory) {
      setStory(foundStory);
      setSelectedWordsList(foundStory.words || []);
    }
  }, [id]);

  useEffect(() => {
    // Simulate reading progress
    const timer = setTimeout(() => {
      setReadingProgress(75);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleWordClick = (word, index) => {
    const updatedWords = [...selectedWordsList];
    updatedWords[index] = { ...word, selected: !word.selected };
    setSelectedWordsList(updatedWords);
    
    addSelectedWord(word);
    
    toast({
      title: word.selected ? "Wort entfernt" : "Wort hinzugefügt",
      description: `"${word.word}" ${word.selected ? 'aus den' : 'zu den'} Lernwörtern ${word.selected ? 'entfernt' : 'hinzugefügt'}.`,
      duration: 2000,
    });
  };

  const handleVerbClick = (verb) => {
    setSelectedVerb(verb);
    setActiveTab('story'); // Ensure we can see the verb panel
  };

  const handleGrammarClick = (text, explanation) => {
    setSelectedGrammarText(text);
    setSelectedGrammarExplanation(explanation);
    setShowGrammarExplanationPanel(true);
    // Scroll to the explanation panel
    setTimeout(() => {
      const panel = document.getElementById('grammar-explanation-panel');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleAddToTraining = (item) => {
    setUserTrainingItems(prev => [...prev, { ...item, id: Date.now() }]);
    toast({
      title: "Element hinzugefügt",
      description: "Das Element wurde zu Ihren Trainingsgegenständen hinzugefügt.",
    });
  };

  const handleSentenceClick = (sentence) => {
    setSelectedSentence(sentence);
    setShowGrammarPanel(true);
  };

  const renderInteractiveText = (text) => {
    if (!story || !selectedWordsList.length) {
      return <p className="text-lg leading-relaxed text-slate-800">{text}</p>;
    }

    let processedText = text;
    const sentences = story.sentences || [];
    const grammarExplanationsForStory = grammarExplanations[story.id]?.explanations || [];
    
    // Highlight clickable words
    selectedWordsList.forEach((wordObj, index) => {
      const regex = new RegExp(`\\b${wordObj.word}\\b`, 'gi');
      processedText = processedText.replace(regex, (match) => {
        const isSelected = wordObj.selected;
        return `<span 
          class="word-highlight cursor-pointer px-1 rounded transition-all ${
            isSelected 
              ? 'bg-[#4ECDC4] text-white' 
              : 'bg-yellow-100 hover:bg-yellow-200'
          }" 
          data-word-index="${index}"
          title="${wordObj.translation}"
        >${match}</span>`;
      });
    });

    // Highlight sentences with grammar topics
    sentences.forEach((sentenceObj, index) => {
      if (sentenceObj.grammarTopics && sentenceObj.grammarTopics.length > 0) {
        const sentenceRegex = new RegExp(sentenceObj.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        processedText = processedText.replace(sentenceRegex, (match) => {
          return `<span 
            class="sentence-highlight cursor-pointer border-l-4 border-blue-300 pl-2 hover:bg-blue-50 transition-all block my-2" 
            data-sentence-index="${index}"
            title="Klicken für Grammatikthemen"
          >${match}</span>`;
        });
      }
    });

    // Highlight grammar explanation texts
    grammarExplanationsForStory.forEach((explanation, index) => {
      const textRegex = new RegExp(explanation.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      processedText = processedText.replace(textRegex, (match) => {
        return `<span 
          class="grammar-highlight cursor-pointer bg-green-100 hover:bg-green-200 border-b-2 border-green-400 transition-all" 
          data-grammar-index="${index}"
          title="Klicken für Grammatikerklärung: ${explanation.title}"
        >${match}</span>`;
      });
    });

    return (
      <div 
        className="text-lg leading-relaxed text-slate-800"
        dangerouslySetInnerHTML={{ __html: processedText }}
        onClick={(e) => {
          const wordIndex = e.target.getAttribute('data-word-index');
          const sentenceIndex = e.target.getAttribute('data-sentence-index');
          const grammarIndex = e.target.getAttribute('data-grammar-index');
          
          if (wordIndex !== null) {
            handleWordClick(selectedWordsList[parseInt(wordIndex)], parseInt(wordIndex));
          }
          
          if (sentenceIndex !== null) {
            handleSentenceClick(sentences[parseInt(sentenceIndex)]);
          }
          
          if (grammarIndex !== null) {
            const explanation = grammarExplanationsForStory[parseInt(grammarIndex)];
            handleGrammarClick(explanation.text, explanation);
          }
        }}
      />
    );
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const selectedWordsCount = selectedWordsList.filter(w => w.selected).length;

  if (!story) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Geschichte wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/stories')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Badge className={getLevelColor(story.level)}>
                  {story.level}
                </Badge>
                <span className="font-medium text-slate-700">{story.readTime}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Lesefortschritt</span>
            <span className="text-sm text-slate-600">{readingProgress}%</span>
          </div>
          <Progress value={readingProgress} className="h-2" />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Story Header with Image */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="aspect-video relative">
                <img 
                  src={story.image} 
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4">
                  <Badge className={getLevelColor(story.level)}>
                    {story.level}
                  </Badge>
                </div>
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-sm">
                  ✓ Story ist gelernt
                </div>
              </div>
            </Card>

            {/* Tab Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="story" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Story
                </TabsTrigger>
                <TabsTrigger value="quiz" className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  Quiz
                </TabsTrigger>
                <TabsTrigger value="keywords" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Keywörter
                </TabsTrigger>
                <TabsTrigger value="grammar" className="flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Grammatik
                </TabsTrigger>
              </TabsList>

              {/* Story Tab */}
              <TabsContent value="story" className="space-y-6">
                {/* Story Header Info */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-2xl font-bold text-slate-900 leading-tight mb-2">
                          {story.title}
                        </CardTitle>
                        <p className="text-slate-600 mb-3">{story.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <span>{story.readTime}</span>
                          <span>•</span>
                          <span>31 August 2025</span>
                          <span>•</span>
                          <span>★ 4.8</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Synchronized Text-to-Speech */}
                <div className="space-y-6">
                  <SynchronizedTextReader
                    text={story.content}
                    language={story.language}
                    onWordHighlight={(word, index) => {
                      // Optional: Zusätzliche Aktionen beim Wort-Highlighting
                      console.log(`Aktuelles Wort: ${word} (Index: ${index})`);
                    }}
                    highlightColor="bg-[#4ECDC4]"
                  />
                </div>

                {/* Interactive Story Content with Word Selection */}
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      {/* German Text with Interactive Elements */}
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3 pb-2 border-b border-slate-200">
                          Deutsche Geschichte (Interaktiv)
                        </h4>
                        <div className="prose prose-lg max-w-none">
                          {story.content.split('\n\n').map((paragraph, index) => (
                            <div key={index} className="mb-4">
                              {renderInteractiveText(paragraph)}
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-blue-800 text-sm">
                            💡 <strong>Interaktive Elemente:</strong>
                            <span className="ml-2">
                              <span className="bg-yellow-100 px-1 rounded">Gelb = Vokabeln</span>
                              <span className="mx-2">•</span>
                              <span className="bg-green-100 px-1 rounded">Grün = Grammatik</span>
                              <span className="mx-2">•</span>
                              <span className="bg-blue-100 px-1 rounded">Blau = Sätze</span>
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* English Translation */}
                      <div className="bg-slate-50 p-6 rounded-lg">
                        <h4 className="font-medium text-slate-900 mb-3 pb-2 border-b border-slate-300">
                          English Translation
                        </h4>
                        <div className="prose prose-lg max-w-none text-slate-700 italic">
                          <p>Have you ever thought of a barn when you hear the word "party"?</p>
                          <p>In rural areas of the UK, it's popular for rich people to have a party barn.</p>
                          <p>Old barns are converted into venues for parties or other social events.</p>
                          <p>These barns are separate from the main building.</p>
                          <p>The trend started when many people moved from the cities to the countryside during the pandemic.</p>
                          <p>They renovated the barns to have more space for guests or to use the space as an office or gym.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Grammar Explanation Panel */}
                <div id="grammar-explanation-panel">
                  <GrammarExplanationPanel
                    explanation={selectedGrammarExplanation}
                    selectedText={selectedGrammarText}
                    isVisible={showGrammarExplanationPanel}
                    onClose={() => {
                      setShowGrammarExplanationPanel(false);
                      setSelectedGrammarExplanation(null);
                      setSelectedGrammarText('');
                    }}
                    onAddToTraining={handleAddToTraining}
                  />
                </div>

                {/* Bottom Controls */}
                <div className="flex items-center justify-center space-x-4 py-4">
                  <Button className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white rounded-full px-6">
                    Story
                  </Button>
                  <Button variant="outline" className="rounded-full px-6">
                    Satz
                  </Button>
                  <Button variant="outline" className="rounded-full px-6">
                    Wort
                  </Button>
                  <Button variant="outline" className="rounded-full px-4">
                    1x
                  </Button>
                </div>
              </TabsContent>

              {/* Quiz Tab */}
              <TabsContent value="quiz">
                <QuizComponent 
                  storyId={story.id}
                  questions={quizData[story.id]?.questions || []}
                  onComplete={(results) => {
                    toast({
                      title: "Quiz abgeschlossen!",
                      description: `Sie haben ${results.score}% der Fragen richtig beantwortet.`,
                    });
                  }}
                />
              </TabsContent>

              {/* Keywords Tab */}
              <TabsContent value="keywords">
                <KeywordsComponent 
                  storyId={story.id}
                  keywords={keywordsData[story.id]?.keywords || []}
                  onAddToTraining={handleAddToTraining}
                />
              </TabsContent>

              {/* Grammar Tab */}
              <TabsContent value="grammar">
                <div className="space-y-6">
                  {grammarExplanations[story.id]?.explanations?.map((explanation, index) => (
                    <Card key={index} className="border-0 shadow-lg">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <Brain className="h-5 w-5 text-[#4ECDC4]" />
                            {explanation.title}
                          </CardTitle>
                          <Badge className={getLevelColor(explanation.level)}>
                            {explanation.level}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="bg-[#4ECDC4] bg-opacity-10 p-4 rounded-lg">
                            <p className="font-medium text-[#4ECDC4]">"{explanation.text}"</p>
                          </div>
                          <p className="text-slate-700">{explanation.explanation}</p>
                          {explanation.examples && (
                            <div>
                              <h4 className="font-medium text-slate-900 mb-2">Beispiele:</h4>
                              <div className="space-y-2">
                                {explanation.examples.map((example, i) => (
                                  <div key={i} className="p-3 bg-blue-50 rounded-lg text-sm">
                                    {example}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )) || (
                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-8 text-center">
                        <Brain className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                          Keine Grammatikerklärungen verfügbar
                        </h3>
                        <p className="text-slate-600">
                          Für diese Geschichte wurden noch keine Grammatikerklärungen erstellt.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Verb Conjugation Panel */}
            {selectedVerb && (
              <div className="sticky top-24">
                <VerbConjugationPanel 
                  verb={selectedVerb}
                  language={story.language}
                  onAddToTraining={handleAddToTraining}
                />
              </div>
            )}

            {/* Selected Words Panel (when no verb is selected) */}
            {!selectedVerb && (
              <Card className="border-0 shadow-lg sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-[#4ECDC4]" />
                      Meine Wörter
                    </span>
                    <Badge variant="secondary">{selectedWordsCount}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedWordsList.length > 0 ? (
                      selectedWordsList.map((word, index) => (
                        <div 
                          key={index}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                            word.selected 
                              ? 'bg-[#4ECDC4] bg-opacity-10 border-[#4ECDC4]' 
                              : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleWordClick(word, index)}
                        >
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{word.word}</div>
                            <div className="text-sm text-slate-600">{word.translation}</div>
                          </div>
                          {word.selected ? (
                            <CheckCircle2 className="h-5 w-5 text-[#4ECDC4]" />
                          ) : (
                            <Circle className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-600 text-sm text-center py-4">
                        Klicke auf Wörter im Text, um sie zu lernen.
                      </p>
                    )}
                  </div>

                  {selectedWordsCount > 0 && (
                    <>
                      <Separator className="my-4" />
                      <Button 
                        className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                        onClick={() => navigate('/training')}
                      >
                        Wörter trainieren ({selectedWordsCount})
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Schnelle Aktionen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleVerbClick('haben')}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Verb "haben" anzeigen
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setSelectedVerb(null)}
                >
                  <Target className="h-4 w-4 mr-2" />
                  Wortauswahl anzeigen
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('quiz')}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Quiz starten
                </Button>
              </CardContent>
            </Card>

            {/* Grammar Panel */}
            {showGrammarPanel && selectedSentence && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Grammatikanalyse</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-900">
                        "{selectedSentence.text}"
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Grammatikthemen:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedSentence.grammarTopics.map((topic, index) => (
                          <Badge key={index} className={getLevelColor(story.level)}>
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowGrammarPanel(false)}
                    >
                      Schließen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Next Story */}
        <div className="mt-12">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6 text-center">
              <h3 className="font-bold text-slate-900 mb-2">Geschichte abgeschlossen!</h3>
              <p className="text-slate-600 mb-4">
                Du hast {selectedWordsCount} Wörter zum Lernen ausgewählt.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                  onClick={() => navigate('/training')}
                >
                  Jetzt trainieren
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/stories')}
                >
                  Nächste Geschichte
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Grammar Explanation Modal */}
        <GrammarExplanationModal 
          isOpen={showGrammarModal}
          onClose={() => setShowGrammarModal(false)}
          explanation={selectedGrammarExplanation}
          selectedText={selectedGrammarText}
          onAddToTraining={handleAddToTraining}
        />
      </div>
    </div>
  );
};

export default StoryReader;